import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PaymentsIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLongOutlined";
import EqualizerIcon from "@mui/icons-material/EqualizerOutlined";
import dayjs from "dayjs";
import { ExpenseApi, ProjectApi, UserApi } from "../../apis";
import { PageHeader, StatCard } from "../../components/ui";
import {
  DateRangeFilter,
  CUSTOM_RANGE,
  formatRange,
} from "../../components/ui";
import { FilterSelect } from "../../components/ui/FilterBar";
import { ChartCard, HoursByProjectChart } from "../../components/charts";
import { EXPENSE_CATEGORIES, isAdminUser, loadSelectableProjects } from "./helpers";

const DATE_FMT = "YYYY-MM-DD";

const money = (value) =>
  `$${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/** Sums `amount` across rows grouped by `key`, biggest first. */
const sumBy = (rows, key) => {
  const totals = new Map();
  rows.forEach((row) => {
    const name = row[key] || "Unassigned";
    totals.set(name, (totals.get(name) || 0) + Number(row.amount || 0));
  });
  return [...totals.entries()]
    .map(([name, amount]) => ({ name, amount: Math.round(amount * 100) / 100 }))
    .sort((a, b) => b.amount - a.amount);
};

/**
 * Expense reporting.
 *
 * The expenses list answers "what did I file?"; this answers "where is the
 * money going?". There is no aggregation endpoint, so the rows are fetched for
 * the chosen window and summarised here, the same way the time dashboard does.
 */
export default function ExpenseReport() {
  const isAdmin = isAdminUser();

  const [range, setRange] = useState(30);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [projectId, setProjectId] = useState("");
  const [category, setCategory] = useState("");

  const [projects, setProjects] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const isCustom = range === CUSTOM_RANGE;
  const endDate = isCustom && to ? to : dayjs().format(DATE_FMT);
  const startDate =
    isCustom && from
      ? from
      : dayjs(endDate)
          .subtract((isCustom ? 30 : range) - 1, "day")
          .format(DATE_FMT);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await ExpenseApi.getAllExpenses({
        startDate,
        endDate,
        projectId,
        category,
      });
      const list = res?.data?.data?.expenses || [];

      setRows(
        list.map((expense) => ({
          id: expense.id,
          date: expense.date,
          amount: Number(expense.amount || 0),
          category: expense.category || "Uncategorised",
          projectName: expense.project?.name || "Unassigned",
          phaseName: expense.phase?.name || "-",
          submittedBy: expense.user?.name || expense.user?.email || "Unknown",
        }))
      );
    } catch (error) {
      console.error("Failed to load expense report:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, projectId, category]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    loadSelectableProjects({ ProjectApi, UserApi })
      .then(setProjects)
      .catch(() => setProjects([]));
  }, []);

  const summary = useMemo(() => {
    const total = rows.reduce((acc, row) => acc + row.amount, 0);

    return {
      total,
      count: rows.length,
      average: rows.length ? total / rows.length : 0,
      byCategory: sumBy(rows, "category"),
      byProject: sumBy(rows, "projectName"),
      byUser: sumBy(rows, "submittedBy"),
    };
  }, [rows]);

  const dateSpan = formatRange(startDate, endDate);

  const downloadCSV = () => {
    const header = ["Date", "Project", "Phase", "Category", "Submitted by", "Amount"];
    const lines = [header.join(",")];

    rows.forEach((row) => {
      const values = [
        row.date ? dayjs(row.date).format(DATE_FMT) : "",
        row.projectName,
        row.phaseName,
        row.category,
        row.submittedBy,
        row.amount.toFixed(2),
      ].map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`);
      lines.push(values.join(","));
    });

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `expense-report-${startDate}-to-${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const filters = (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
      <DateRangeFilter
        value={range}
        onChange={setRange}
        from={from}
        to={to}
        onFromChange={setFrom}
        onToChange={setTo}
      />
      <FilterSelect
        label="Project"
        value={projectId}
        onChange={(event) => setProjectId(event.target.value)}
        options={projects.map((p) => ({ value: String(p.id), label: p.name }))}
        allLabel="All projects"
        minWidth={170}
      />
      <FilterSelect
        label="Category"
        value={category}
        onChange={(event) => setCategory(event.target.value)}
        options={EXPENSE_CATEGORIES.map((c) => ({ value: c, label: c }))}
        allLabel="All categories"
      />
      <Button
        variant="outlined"
        size="small"
        onClick={downloadCSV}
        disabled={!rows.length}
        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
        sx={{ height: 38 }}
      >
        Export CSV
      </Button>
    </Box>
  );

  const breakdownTable = (title, data, label) => (
    <TableContainer sx={{ maxHeight: 320, overflowX: "auto" }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>{label}</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell align="right">Share</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row) => (
            <TableRow key={row.name} hover>
              <TableCell sx={{ maxWidth: 200 }}>
                <Typography variant="body2" noWrap title={row.name}>
                  {row.name}
                </Typography>
              </TableCell>
              <TableCell align="right">{money(row.amount)}</TableCell>
              <TableCell align="right">
                {summary.total ? `${Math.round((row.amount / summary.total) * 100)}%` : "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <PageHeader
        title="Expense Report"
        subtitle={`Spending · ${dateSpan}`}
        actions={filters}
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <StatCard
                label="Total spend"
                value={money(summary.total)}
                icon={PaymentsIcon}
                color="primary"
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <StatCard
                label="Expenses"
                value={summary.count}
                icon={ReceiptLongIcon}
                color="violet"
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <StatCard
                label="Average"
                value={money(summary.average)}
                icon={EqualizerIcon}
                color="teal"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <ChartCard
                title="By category"
                isEmpty={!summary.byCategory.length}
              >
                <HoursByProjectChart
                  data={summary.byCategory}
                  dataKey="amount"
                  seriesName="Amount"
                  formatValue={money}
                  formatLabel={(value) => Number(value).toFixed(0)}
                  height={280}
                />
              </ChartCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <ChartCard
                title="By project"
                isEmpty={!summary.byProject.length}
              >
                <HoursByProjectChart
                  data={summary.byProject}
                  dataKey="amount"
                  seriesName="Amount"
                  formatValue={money}
                  formatLabel={(value) => Number(value).toFixed(0)}
                  height={280}
                />
              </ChartCard>
            </Grid>

            {isAdmin && (
              <Grid item xs={12}>
                <ChartCard
                  title="By person"
                  isEmpty={!summary.byUser.length}
                >
                  {breakdownTable("Person", summary.byUser, "Person")}
                </ChartCard>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </Box>
  );
}
